import type { ComponentChildren, ComponentType, JSX, Ref } from 'preact';
import { useId } from 'preact/hooks';
import type { LucideProps } from 'lucide-preact';
import { cn } from '@/lib/cn';
import { Icon } from './Icon';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputState = 'default' | 'success' | 'warning' | 'error';

export interface InputProps extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  state?: InputState;
  invalid?: boolean;
  leftIcon?: ComponentType<LucideProps>;
  rightIcon?: ComponentType<LucideProps>;
  leftAddon?: ComponentChildren;
  rightAddon?: ComponentChildren;
  inputRef?: Ref<HTMLInputElement>;
  hint?: ComponentChildren;
  label?: ComponentChildren;
}

const SIZE: Record<InputSize, { root: string; text: string }> = {
  sm: { root: 'h-8 px-2.5', text: 'text-xs' },
  md: { root: 'h-9 px-3.5', text: 'text-13' },
  lg: { root: 'h-12 px-4', text: 'text-base' },
};

const STATE: Record<InputState, string> = {
  default:
    'border-border hover:border-border-strong focus-within:border-brand focus-within:shadow-focus',
  success:
    'border-ok hover:border-ok focus-within:border-ok focus-within:shadow-focus-ok',
  // Reunión 2026-05-13 (cliente): warning para resaltar inputs con datos
  // críticos del Método QQ sin rellenar — borde y bg ámbar, sin escalar a
  // error visual. Si zod falla `invalid` (error) gana sobre warning.
  warning:
    'border-warning bg-warning-bg/40 hover:border-warning focus-within:border-warning focus-within:shadow-focus-warning',
  error:
    'border-error hover:border-error focus-within:border-error focus-within:shadow-focus-error',
};

const ROOT_BASE =
  'flex min-w-0 items-center w-full gap-2 rounded-md border bg-surface text-fg transition-[border-color,box-shadow,background,transform] duration-med ease-snappy focus-within:scale-[1.002]';
const INPUT_BASE =
  'flex-1 min-w-0 bg-transparent border-0 outline-none placeholder:text-fg-faint disabled:cursor-not-allowed disabled:opacity-60';

export function Input({
  inputSize = 'md',
  state,
  invalid,
  leftIcon,
  rightIcon,
  leftAddon,
  rightAddon,
  className,
  disabled,
  inputRef,
  hint,
  label,
  ...rest
}: InputProps) {
  const sz = SIZE[inputSize];
  const resolvedState = invalid ? 'error' : state ?? 'default';
  const st = STATE[resolvedState];
  const iconSize = inputSize === 'sm' ? 14 : inputSize === 'lg' ? 18 : 16;
  const iconColor =
    resolvedState === 'success' ? 'text-ok' :
    resolvedState === 'error' ? 'text-error' :
    resolvedState === 'warning' ? 'text-warning' :
    'text-fg-faint';

  // A11y: enlazamos label ↔ input por id, y hint ↔ input por aria-describedby.
  // Respetamos un id explícito pasado por `rest` si lo hubiera.
  const generatedId = useId();
  const inputId = (rest.id as string | undefined) ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={inputId} className="block text-13 font-semibold text-fg-strong mb-2">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          ROOT_BASE,
          sz.root,
          sz.text,
          st,
          disabled && 'opacity-60 pointer-events-none bg-surface-2 border-border-subtle',
          className,
        )}
      >
        {leftIcon ? <Icon as={leftIcon} size={iconSize} className={iconColor} /> : null}
        {leftAddon ? <span className="text-fg-muted text-xs font-medium">{leftAddon}</span> : null}
        <input
          ref={inputRef}
          disabled={disabled}
          aria-invalid={resolvedState === 'error'}
          aria-describedby={hintId}
          className={INPUT_BASE}
          {...rest}
          id={inputId}
        />
        {rightAddon ? <span className="text-fg-muted text-xs font-medium">{rightAddon}</span> : null}
        {rightIcon ? <Icon as={rightIcon} size={iconSize} className={iconColor} /> : null}
      </div>
      {hint ? (
        <p
          id={hintId}
          className={cn(
            'text-xs mt-1.5',
            resolvedState === 'error' ? 'text-error' :
            resolvedState === 'success' ? 'text-ok' :
            resolvedState === 'warning' ? 'text-warning' :
            'text-fg-muted',
          )}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
